import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Confirm,
    Identifier,
    ShowBase,
    useCanAccess,
    useDataProvider,
    useGetList,
    useNotify,
    useShowContext,
    useUpdate,
} from 'react-admin';

import type { CrmDataProvider } from '@/providers/types';
import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import {
    defaultLeadStatuses,
    leadStatusEditChoices,
} from '@/root/defaultConfiguration';
import { formatRelativeBackendDate } from '@/utils/date_utils';
import type { Lead, LeadLog, LeadNextStepStatus, QuoteJobFolder } from '@/types';
import { LeadAside } from './LeadAside';
import { LeadNextStepsStatusList } from './LeadNextStepsStatusList';
import { LeadSourceGallery } from './LeadSourceGallery';
import { leadApiErrorMessage } from './leadApi';
import { effectiveLeadSources } from './leadSourceUtils';

const statusLabel = (status: string) =>
    defaultLeadStatuses.find(item => item.value === status)?.label || status;

const LeadLogs = ({
    leadId,
    refreshKey,
}: {
    leadId: number;
    refreshKey?: string;
}) => {
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [logs, setLogs] = useState<LeadLog[]>([]);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState<Identifier | null>(null);
    const [editText, setEditText] = useState('');
    const [pendingDeleteId, setPendingDeleteId] = useState<Identifier | null>(null);
    const { canAccess: canUpdate } = useCanAccess({
        resource: 'lead_log',
        action: 'edit',
    });

    const reload = async () => {
        const rows = await dataProvider.getLeadLogs(leadId);
        setLogs(rows);
    };

    useEffect(() => {
        reload().catch(() => setLogs([]));
    }, [leadId, refreshKey, dataProvider]);

    const handleAdd = async () => {
        const text = draft.trim();
        if (!text) return;
        setSaving(true);
        try {
            await dataProvider.createLeadLog(leadId, text);
            setDraft('');
            notify('Log added', { type: 'info' });
            await reload();
        } catch {
            notify('Failed to add log', { type: 'warning' });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEdit = async () => {
        if (editingId == null || !editText.trim()) return;
        setSaving(true);
        try {
            await dataProvider.updateLeadLog(leadId, editingId, editText.trim());
            setEditingId(null);
            notify('Log updated', { type: 'info' });
            await reload();
        } catch {
            notify('Failed to update log', { type: 'warning' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (pendingDeleteId == null) return;
        setSaving(true);
        try {
            await dataProvider.deleteLeadLog(leadId, pendingDeleteId);
            setPendingDeleteId(null);
            notify('Log deleted', { type: 'info' });
            await reload();
        } catch {
            notify('Failed to delete log', { type: 'warning' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Typography variant="subtitle1" mb={1}>
                Contact log
            </Typography>
            {canUpdate ? (
                <>
                    <TextField
                        label="Add a contact note"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        margin="dense"
                    />
                    <Box mt={1} display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            onClick={handleAdd}
                            disabled={saving || !draft.trim()}
                        >
                            {saving ? <CircularProgress size={20} /> : 'Add note'}
                        </Button>
                    </Box>
                </>
            ) : null}
            {logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mb={2}>
                    No contact notes yet
                </Typography>
            ) : (
                <Stack spacing={1.5} mt={2}>
                    {logs.map(item => (
                        <Box
                            key={item.id}
                            sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
                        >
                            {editingId === item.id ? (
                                <>
                                    <TextField
                                        value={editText}
                                        onChange={e => setEditText(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        size="small"
                                    />
                                    <Stack direction="row" spacing={1} mt={1}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleSaveEdit}
                                            disabled={saving}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => setEditingId(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </Stack>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" whiteSpace="pre-wrap">
                                        {item.text}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.created_by_name || 'Unknown'}
                                        {item.created_at
                                            ? ` · ${formatRelativeBackendDate(item.created_at)}`
                                            : ''}
                                    </Typography>
                                    {canUpdate ? (
                                        <Stack direction="row" spacing={1} mt={0.5}>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setEditingId(item.id);
                                                    setEditText(item.text);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => setPendingDeleteId(item.id)}
                                                disabled={saving}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    ) : null}
                                </>
                            )}
                        </Box>
                    ))}
                </Stack>
            )}

            <Confirm
                isOpen={pendingDeleteId != null}
                loading={saving}
                title="Delete contact note"
                content="Delete this contact log entry? This cannot be undone."
                confirm="Delete"
                confirmColor="warning"
                onConfirm={handleDelete}
                onClose={() => setPendingDeleteId(null)}
            />
        </>
    );
};

export const LeadShow = () => (
    <ShowBase queryOptions={{ meta: { includes: 'lead_next_steps' } }}>
        <LeadShowContent />
    </ShowBase>
);

const LeadShowContent = () => {
    const { record, isPending, error, refetch } = useShowContext<Lead>();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [update] = useUpdate();
    const { canAccess: canReadLogs } = useCanAccess({
        resource: 'lead_log',
        action: 'show',
    });
    const { canAccess: canCreateQuote } = useCanAccess({
        resource: 'quotes',
        action: 'create',
    });
    const { canAccess: canReadProjects } = useCanAccess({
        resource: 'projects',
        action: 'list',
    });
    const { data: relatedProjects = [], isPending: projectsPending } = useGetList<QuoteJobFolder>(
        'projects',
        {
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'id', order: 'DESC' },
            filter: { lead_id: record?.id },
        },
        { enabled: Boolean(record?.id) && canReadProjects !== false },
    );

    if (isPending) return null;
    if (error || !record) {
        return (
            <ResourceNotFound
                resourceLabel="Lead"
                listPath="/leads"
                listLabel="Back to leads"
            />
        );
    }

    const handleStepStatus = async (stepId: number, status: LeadNextStepStatus) => {
        try {
            await dataProvider.patchLeadNextStepStatus(record.id, stepId, status);
            notify('Next step updated', { type: 'info' });
            refetch();
        } catch (err) {
            notify(leadApiErrorMessage(err, 'Failed to update next step'), {
                type: 'warning',
            });
        }
    };

    return (
        <Box mt={2} mb={2} display="flex" flexWrap="wrap">
            <LeadAside />
            <Box flex="1" maxWidth={800}>
                <Card>
                    <CardContent>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            mb={2}
                            gap={2}
                        >
                            <Box>
                                <Typography variant="h5" mb={1}>
                                    {record.name?.trim() || 'Unnamed lead'}
                                </Typography>
                                <Chip size="small" label={statusLabel(record.status)} />
                            </Box>
                            <Stack direction="column" gap={1} alignItems="flex-start">
                                {canCreateQuote ? (
                                    <Button
                                        component={RouterLink}
                                        to={`/quotes/create?lead_id=${record.id}`}
                                        variant="contained"
                                        size="small"
                                    >
                                        {record.status === 'done'
                                            ? 'Create a new quote'
                                            : 'Convert to quote'}
                                    </Button>
                                ) : null}
                                <TextField
                                    select
                                    size="small"
                                    label="Status"
                                    value={record.status}
                                    onChange={event => {
                                        update(
                                            'leads',
                                            {
                                                id: record.id,
                                                data: {
                                                    ...record,
                                                    status: event.target.value,
                                                    next_steps: record.next_steps || [],
                                                },
                                                previousData: record,
                                            },
                                            {
                                                onError: err =>
                                                    notify(
                                                        leadApiErrorMessage(
                                                            err,
                                                            'Failed to update status'
                                                        ),
                                                        { type: 'warning' }
                                                    ),
                                            }
                                        );
                                    }}
                                    sx={{ minWidth: 140 }}
                                >
                                    {leadStatusEditChoices(record.status).map(item => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Stack>
                        <Typography variant="body2">{record.email || '—'}</Typography>
                        <Typography variant="body2">
                            {record.phone_number || '—'}
                        </Typography>
                        {record.requirements ? (
                            <Typography variant="body1" whiteSpace="pre-wrap" mt={2}>
                                {record.requirements}
                            </Typography>
                        ) : null}

                        {(() => {
                            const sources = effectiveLeadSources(
                                record.source_paths,
                                record.source_path
                            );
                            if (!sources.length) return null;
                            return (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" mb={1}>
                                        Sources
                                    </Typography>
                                    <LeadSourceGallery
                                        leadId={Number(record.id)}
                                        sources={sources}
                                    />
                                </>
                            );
                        })()}

                    </CardContent>
                </Card>

                <Divider sx={{ my: 2 }} />

                <Card>
                    <CardContent>

                        <Typography variant="subtitle1" mb={1}>
                            Next steps
                        </Typography>
                        <LeadNextStepsStatusList
                            steps={record.next_steps}
                            onStatus={handleStepStatus}
                        />
                    </CardContent>
                </Card>

                {canReadProjects !== false ? (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" mb={1}>
                                    Related projects
                                </Typography>
                                {projectsPending ? (
                                    <CircularProgress size={20} />
                                ) : relatedProjects.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No linked projects yet
                                    </Typography>
                                ) : (
                                    <List dense disablePadding>
                                        {relatedProjects.map(project => (
                                            <ListItem key={project.id} disablePadding>
                                                <ListItemButton
                                                    component={RouterLink}
                                                    to={`/quotes/folders/${project.id}`}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            project.project_no != null
                                                                ? `#${project.project_no} · ${project.name}`
                                                                : project.name
                                                        }
                                                        secondary={project.address}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </>
                ) : null}

                <Divider sx={{ my: 2 }} />

                {canReadLogs ? (
                    <Card>
                        <CardContent>
                            <LeadLogs
                                leadId={Number(record.id)}
                                refreshKey={(record.next_steps || [])
                                    .map(step => `${step.id}:${step.status}`)
                                    .join(',')}
                            />
                        </CardContent>
                    </Card>
                ) : null}
            </Box>
        </Box>
    );
};
